// ── Enums (must match backend values exactly) ──

export const AutomationRuleType = {
  Trigger: 1,
  Automation: 2,
} as const;

export type AutomationRuleType = (typeof AutomationRuleType)[keyof typeof AutomationRuleType];

export const AutomationRuleTypeLabels: Record<AutomationRuleType, string> = {
  [AutomationRuleType.Trigger]: 'Trigger',
  [AutomationRuleType.Automation]: 'Scheduled',
};

export const AutomationEvent = {
  TicketCreated: 1,
  TicketUpdated: 2,
  TicketCommentAdded: 3,
  TicketAssigned: 4,
  TicketStatusChanged: 5,
  TicketPriorityChanged: 6,
  TicketCategoryChanged: 7,
  ArticleLinkedToTicket: 8,
  ArticleFeedbackSubmitted: 9,
  ArticleCreated: 10,
  ArticleUpdated: 11,
  ArticlePublished: 12,
  TicketMerged: 13,
} as const;

export type AutomationEvent = (typeof AutomationEvent)[keyof typeof AutomationEvent];

export const AutomationEventLabels: Record<AutomationEvent, string> = {
  [AutomationEvent.TicketCreated]: 'Ticket Created',
  [AutomationEvent.TicketUpdated]: 'Ticket Updated',
  [AutomationEvent.TicketCommentAdded]: 'Comment Added',
  [AutomationEvent.TicketAssigned]: 'Ticket Assigned',
  [AutomationEvent.TicketStatusChanged]: 'Status Changed',
  [AutomationEvent.TicketPriorityChanged]: 'Priority Changed',
  [AutomationEvent.TicketCategoryChanged]: 'Category Changed',
  [AutomationEvent.ArticleLinkedToTicket]: 'Article Linked',
  [AutomationEvent.TicketMerged]: 'Ticket Merged',
  [AutomationEvent.ArticleFeedbackSubmitted]: 'Article Feedback',
  [AutomationEvent.ArticleCreated]: 'Article Created',
  [AutomationEvent.ArticleUpdated]: 'Article Updated',
  [AutomationEvent.ArticlePublished]: 'Article Published',
};

export const AutomationActionType = {
  // Assignment
  AssignToAgent: 1,
  AssignRoundRobin: 2,
  Unassign: 3,
  AssignLoadBalanced: 4,
  // Field modifications
  SetStatus: 10,
  SetPriority: 11,
  SetCategory: 12,
  // In-app notifications
  NotifyAssignee: 20,
  NotifyFollowers: 21,
  NotifyAllAgents: 22,
  NotifySpecificAgents: 24,
  // Email notifications
  SendEmailToAssignee: 30,
  SendEmailToFollowers: 31,
  SendEmailToAllAgents: 32,
  SendEmailToCustomer: 33,
  SendEmailToSpecificAgents: 34,
  // Collaboration
  AddFollower: 40,
  // Article-specific
  NotifyArticleAuthor: 50,
  SendEmailToArticleAuthor: 51,
  // Tracking
  RecordReminder: 54,
} as const;

export type AutomationActionType = (typeof AutomationActionType)[keyof typeof AutomationActionType];

export const AutomationActionTypeLabels: Record<AutomationActionType, string> = {
  [AutomationActionType.AssignToAgent]: 'Assign to Agent',
  [AutomationActionType.AssignRoundRobin]: 'Assign Round Robin',
  [AutomationActionType.Unassign]: 'Unassign',
  [AutomationActionType.AssignLoadBalanced]: 'Assign Load Balanced',
  [AutomationActionType.SetStatus]: 'Set Status',
  [AutomationActionType.SetPriority]: 'Set Priority',
  [AutomationActionType.SetCategory]: 'Set Category',
  [AutomationActionType.NotifyAssignee]: 'Notify Assignee',
  [AutomationActionType.NotifyFollowers]: 'Notify Followers',
  [AutomationActionType.NotifyAllAgents]: 'Notify All Agents',
  [AutomationActionType.NotifySpecificAgents]: 'Notify Specific Agents',
  [AutomationActionType.SendEmailToAssignee]: 'Email Assignee',
  [AutomationActionType.SendEmailToFollowers]: 'Email Followers',
  [AutomationActionType.SendEmailToAllAgents]: 'Email All Agents',
  [AutomationActionType.SendEmailToCustomer]: 'Email Customer',
  [AutomationActionType.SendEmailToSpecificAgents]: 'Email Specific Agents',
  [AutomationActionType.AddFollower]: 'Add Follower',
  [AutomationActionType.NotifyArticleAuthor]: 'Notify Article Author',
  [AutomationActionType.SendEmailToArticleAuthor]: 'Email Article Author',
  [AutomationActionType.RecordReminder]: 'Record Reminder',
};

export const AutomationActionTypeGroup: Record<AutomationActionType, string> = {
  [AutomationActionType.AssignToAgent]: 'Assignment',
  [AutomationActionType.AssignRoundRobin]: 'Assignment',
  [AutomationActionType.Unassign]: 'Assignment',
  [AutomationActionType.AssignLoadBalanced]: 'Assignment',
  [AutomationActionType.SetStatus]: 'Field Modification',
  [AutomationActionType.SetPriority]: 'Field Modification',
  [AutomationActionType.SetCategory]: 'Field Modification',
  [AutomationActionType.NotifyAssignee]: 'In-App Notification',
  [AutomationActionType.NotifyFollowers]: 'In-App Notification',
  [AutomationActionType.NotifyAllAgents]: 'In-App Notification',
  [AutomationActionType.NotifySpecificAgents]: 'In-App Notification',
  [AutomationActionType.SendEmailToAssignee]: 'Email Notification',
  [AutomationActionType.SendEmailToFollowers]: 'Email Notification',
  [AutomationActionType.SendEmailToAllAgents]: 'Email Notification',
  [AutomationActionType.SendEmailToCustomer]: 'Email Notification',
  [AutomationActionType.SendEmailToSpecificAgents]: 'Email Notification',
  [AutomationActionType.AddFollower]: 'Collaboration',
  [AutomationActionType.NotifyArticleAuthor]: 'In-App Notification',
  [AutomationActionType.SendEmailToArticleAuthor]: 'Email Notification',
  [AutomationActionType.RecordReminder]: 'Tracking',
};

export const ConditionField = {
  // Ticket properties
  Status: 1,
  Priority: 2,
  CategoryId: 3,
  AssignedToId: 4,
  Subject: 5,
  Description: 6,
  IsOverdue: 7,
  DueAt: 8,
  // Customer properties
  CustomerEmail: 20,
  CustomerCompany: 21,
  // Source/channel
  CreatedByType: 30,
  IsCommentInternal: 31,
  // Article properties (KB events)
  ArticleTitle: 35,
  ArticleCollectionId: 36,
  ArticleVisibility: 37,
  FeedbackIsHelpful: 38,
  // Time-based (Automations only)
  HoursSinceCreated: 40,
  HoursSinceUpdated: 41,
  HoursSinceStatusChange: 42,
  HoursSinceAssigned: 43,
  HoursSinceLastActivity: 44,
  HoursUntilDue: 45,
  ReminderCountSinceLastActivity: 46,
  // Time-based (Automations only) — Article
  HoursSinceArticleCreated: 60,
  HoursSinceArticleUpdated: 61,
  HoursSinceArticlePublished: 62,
} as const;

export type ConditionField = (typeof ConditionField)[keyof typeof ConditionField];

export const ConditionFieldLabels: Record<ConditionField, string> = {
  [ConditionField.Status]: 'Status',
  [ConditionField.Priority]: 'Priority',
  [ConditionField.CategoryId]: 'Category',
  [ConditionField.AssignedToId]: 'Assigned To',
  [ConditionField.Subject]: 'Subject',
  [ConditionField.Description]: 'Description',
  [ConditionField.IsOverdue]: 'Is Overdue',
  [ConditionField.DueAt]: 'Due Date',
  [ConditionField.CustomerEmail]: 'Customer Email',
  [ConditionField.CustomerCompany]: 'Customer Company',
  [ConditionField.CreatedByType]: 'Created By Type',
  [ConditionField.IsCommentInternal]: 'Is Internal Comment',
  [ConditionField.ArticleTitle]: 'Article Title',
  [ConditionField.ArticleCollectionId]: 'Article Collection',
  [ConditionField.ArticleVisibility]: 'Article Visibility',
  [ConditionField.FeedbackIsHelpful]: 'Feedback Is Helpful',
  [ConditionField.HoursSinceArticleCreated]: 'Time Since Article Created',
  [ConditionField.HoursSinceArticleUpdated]: 'Time Since Article Updated',
  [ConditionField.HoursSinceArticlePublished]: 'Time Since Article Published',
  [ConditionField.HoursSinceCreated]: 'Time Since Created',
  [ConditionField.HoursSinceUpdated]: 'Time Since Updated',
  [ConditionField.HoursSinceStatusChange]: 'Time Since Status Change',
  [ConditionField.HoursSinceAssigned]: 'Time Since Assigned',
  [ConditionField.HoursSinceLastActivity]: 'Time Since Last Activity',
  [ConditionField.HoursUntilDue]: 'Time Until Due',
  [ConditionField.ReminderCountSinceLastActivity]: 'Reminder Count Since Last Activity',
};

export const ConditionFieldGroup: Record<ConditionField, string> = {
  [ConditionField.Status]: 'Ticket',
  [ConditionField.Priority]: 'Ticket',
  [ConditionField.CategoryId]: 'Ticket',
  [ConditionField.AssignedToId]: 'Ticket',
  [ConditionField.Subject]: 'Ticket',
  [ConditionField.Description]: 'Ticket',
  [ConditionField.IsOverdue]: 'Ticket',
  [ConditionField.DueAt]: 'Ticket',
  [ConditionField.CustomerEmail]: 'Customer',
  [ConditionField.CustomerCompany]: 'Customer',
  [ConditionField.CreatedByType]: 'Source',
  [ConditionField.IsCommentInternal]: 'Source',
  [ConditionField.ArticleTitle]: 'Article',
  [ConditionField.ArticleCollectionId]: 'Article',
  [ConditionField.ArticleVisibility]: 'Article',
  [ConditionField.FeedbackIsHelpful]: 'Article',
  [ConditionField.HoursSinceArticleCreated]: 'Time-Based',
  [ConditionField.HoursSinceArticleUpdated]: 'Time-Based',
  [ConditionField.HoursSinceArticlePublished]: 'Time-Based',
  [ConditionField.HoursSinceCreated]: 'Time-Based',
  [ConditionField.HoursSinceUpdated]: 'Time-Based',
  [ConditionField.HoursSinceStatusChange]: 'Time-Based',
  [ConditionField.HoursSinceAssigned]: 'Time-Based',
  [ConditionField.HoursSinceLastActivity]: 'Time-Based',
  [ConditionField.HoursUntilDue]: 'Time-Based',
  [ConditionField.ReminderCountSinceLastActivity]: 'Time-Based',
};

export const ConditionOperator = {
  Equals: 1,
  NotEquals: 2,
  Contains: 3,
  NotContains: 4,
  GreaterThan: 5,
  LessThan: 6,
  IsSet: 7,
  IsNotSet: 8,
  In: 9,
  NotIn: 10,
  // Tree fields (ticket category, article collection): the value or anything under it
  IsWithin: 11,
  IsNotWithin: 12,
} as const;

export type ConditionOperator = (typeof ConditionOperator)[keyof typeof ConditionOperator];

export const ConditionOperatorLabels: Record<ConditionOperator, string> = {
  [ConditionOperator.Equals]: 'Equals',
  [ConditionOperator.NotEquals]: 'Not Equals',
  [ConditionOperator.Contains]: 'Contains',
  [ConditionOperator.NotContains]: 'Not Contains',
  [ConditionOperator.GreaterThan]: 'Greater Than',
  [ConditionOperator.LessThan]: 'Less Than',
  [ConditionOperator.IsSet]: 'Is Set',
  [ConditionOperator.IsNotSet]: 'Is Not Set',
  [ConditionOperator.In]: 'In',
  [ConditionOperator.NotIn]: 'Not In',
  [ConditionOperator.IsWithin]: 'Is Within',
  [ConditionOperator.IsNotWithin]: 'Is Not Within',
};

// What the tree operators mean, shown under the value picker
export const ConditionOperatorHints: Partial<Record<ConditionOperator, string>> = {
  [ConditionOperator.IsWithin]: 'Matches it and anything under it (subcategories or sections)',
  [ConditionOperator.IsNotWithin]: 'Matches anything outside it and everything under it, including no value',
};

export const ConditionFieldDescriptions: Partial<Record<ConditionField, string>> = {
  [ConditionField.Status]: 'Current ticket status (Open, In Progress, etc.)',
  [ConditionField.Priority]: 'Ticket priority level',
  [ConditionField.CategoryId]: 'The ticket\'s category. "Is Within" also matches its subcategories',
  [ConditionField.ArticleTitle]: 'The article\'s title',
  [ConditionField.ArticleCollectionId]: 'The knowledge base collection the article is filed under. "Is Within" also matches its sections',
  [ConditionField.ArticleVisibility]: 'Whether the article is public or internal',
  [ConditionField.FeedbackIsHelpful]: 'Whether the customer marked the article helpful',
  [ConditionField.AssignedToId]: 'The agent currently assigned to the ticket',
  [ConditionField.Subject]: 'The ticket subject line',
  [ConditionField.Description]: 'The ticket description body',
  [ConditionField.IsOverdue]: 'Whether the ticket has passed its due date',
  [ConditionField.DueAt]: 'The ticket\'s due date',
  [ConditionField.CustomerEmail]: 'Email address of the ticket\'s customer',
  [ConditionField.CustomerCompany]: 'Company name of the ticket\'s customer',
  [ConditionField.CreatedByType]: 'Who created the ticket (Agent, Customer, or System)',
  [ConditionField.IsCommentInternal]: 'Whether the comment is internal (not visible to customer)',
  [ConditionField.HoursSinceCreated]: 'How long ago the ticket was created',
  [ConditionField.HoursSinceUpdated]: 'How long since the ticket was last modified',
  [ConditionField.HoursSinceStatusChange]: 'How long the ticket has been in its current status',
  [ConditionField.HoursSinceAssigned]: 'How long since the ticket was assigned to its current agent',
  [ConditionField.HoursSinceLastActivity]: 'How long since the last comment or update on the ticket',
  [ConditionField.HoursUntilDue]: 'How much time remains before the ticket\'s due date',
  [ConditionField.ReminderCountSinceLastActivity]: 'Number of reminders recorded since the last comment or update. Resets to 0 when new activity occurs. Requires "Run every time" repeat behavior and a Record Reminder action.',
};

export const AutomationActionTypeDescriptions: Partial<Record<AutomationActionType, string>> = {
  [AutomationActionType.AssignToAgent]: 'Assigns the ticket to a specific agent',
  [AutomationActionType.AssignRoundRobin]: 'Assigns to the next agent in rotation order - everyone assignable, or only the agents you pick',
  [AutomationActionType.Unassign]: 'Removes the current agent assignment',
  [AutomationActionType.AssignLoadBalanced]: 'Assigns to the agent with the fewest open tickets - everyone assignable, or only the agents you pick',
  [AutomationActionType.SetStatus]: 'Changes the ticket status',
  [AutomationActionType.SetPriority]: 'Changes the ticket priority level',
  [AutomationActionType.SetCategory]: 'Changes the ticket category',
  [AutomationActionType.NotifyAssignee]: 'Sends an in-app notification to the assigned agent. In time-based rules, uses status-aware reminder notifications.',
  [AutomationActionType.NotifyFollowers]: 'Sends in-app notifications to all ticket followers. In time-based rules, uses status-aware reminder notifications.',
  [AutomationActionType.NotifyAllAgents]: 'Sends in-app notifications to all agents in the organization. In time-based rules, uses status-aware reminder notifications.',
  [AutomationActionType.NotifySpecificAgents]: 'Sends in-app notifications to selected agents. In time-based rules, uses status-aware reminder notifications.',
  [AutomationActionType.SendEmailToAssignee]: 'Sends an email to the assigned agent. In time-based rules, uses status-aware templates (overdue, SLA warning, or inactivity reminder).',
  [AutomationActionType.SendEmailToFollowers]: 'Sends email notifications to all ticket followers. In time-based rules, uses status-aware reminder templates.',
  [AutomationActionType.SendEmailToAllAgents]: 'Sends email notifications to all agents in the organization. In time-based rules, uses open-ticket reminder templates.',
  [AutomationActionType.SendEmailToCustomer]: 'Sends an email to the ticket\'s customer. In time-based rules, uses an inactivity reminder template.',
  [AutomationActionType.SendEmailToSpecificAgents]: 'Sends email notifications to selected agents. In time-based rules, uses status-aware reminder templates.',
  [AutomationActionType.AddFollower]: 'Adds an agent as a follower on the ticket',
  [AutomationActionType.NotifyArticleAuthor]: 'Sends an in-app notification to the author of the article',
  [AutomationActionType.SendEmailToArticleAuthor]: 'Sends an email notification to the author of the article',
  [AutomationActionType.RecordReminder]: 'Increments the reminder counter for this ticket. Use with the Reminder Count condition and "Run every time" repeat behavior to cap how many reminders are sent.',
};

export const AutomationRuleTypeDescriptions: Record<AutomationRuleType, string> = {
  [AutomationRuleType.Trigger]: 'Fires immediately when a specific event occurs',
  [AutomationRuleType.Automation]: 'Runs periodically on a background schedule',
};

export const AutomationEventDescriptions: Record<AutomationEvent, string> = {
  [AutomationEvent.TicketCreated]: 'When a new ticket is created',
  [AutomationEvent.TicketUpdated]: 'When any ticket field is modified',
  [AutomationEvent.TicketCommentAdded]: 'When a comment is added to a ticket',
  [AutomationEvent.TicketAssigned]: 'When a ticket is assigned or reassigned',
  [AutomationEvent.TicketStatusChanged]: 'When the ticket status changes',
  [AutomationEvent.TicketPriorityChanged]: 'When the ticket priority changes',
  [AutomationEvent.TicketCategoryChanged]: 'When the ticket category changes',
  [AutomationEvent.ArticleLinkedToTicket]: 'When a knowledge base article is linked to a ticket',
  [AutomationEvent.TicketMerged]: 'When one ticket is merged into another — fires on both the closed ticket and the surviving one',
  [AutomationEvent.ArticleFeedbackSubmitted]: 'When a customer submits feedback on a knowledge base article',
  [AutomationEvent.ArticleCreated]: 'When a new knowledge base article is created',
  [AutomationEvent.ArticleUpdated]: 'When a knowledge base article is updated',
  [AutomationEvent.ArticlePublished]: 'When a knowledge base article is published',
};

export const RepeatBehavior = {
  RunOncePerTicket: 1,
  RunEveryTime: 2,
} as const;

export type RepeatBehavior = (typeof RepeatBehavior)[keyof typeof RepeatBehavior];

export const RepeatBehaviorLabels: Record<RepeatBehavior, string> = {
  [RepeatBehavior.RunOncePerTicket]: 'Run once per ticket',
  [RepeatBehavior.RunEveryTime]: 'Run every time',
};

export const RepeatBehaviorDescriptions: Record<RepeatBehavior, string> = {
  [RepeatBehavior.RunOncePerTicket]: 'Only fires once per ticket until new activity occurs (comment, update, etc.). Not compatible with multi-step reminder patterns.',
  [RepeatBehavior.RunEveryTime]: 'Fires on every background sweep — required for rules using Reminder Count to send multiple reminders before activity resets the counter.',
};

// ── Helper functions ──

export type ConditionValueType = 'enum' | 'text' | 'number' | 'duration' | 'boolean' | 'id' | 'tree' | 'date';

export function getConditionFieldValueType(field: ConditionField): ConditionValueType {
  switch (field) {
    case ConditionField.Status:
    case ConditionField.Priority:
    case ConditionField.CreatedByType:
    case ConditionField.ArticleVisibility:
      return 'enum';
    case ConditionField.IsOverdue:
    case ConditionField.IsCommentInternal:
    case ConditionField.FeedbackIsHelpful:
      return 'boolean';
    case ConditionField.HoursSinceCreated:
    case ConditionField.HoursSinceUpdated:
    case ConditionField.HoursSinceStatusChange:
    case ConditionField.HoursSinceAssigned:
    case ConditionField.HoursSinceLastActivity:
    case ConditionField.HoursUntilDue:
    case ConditionField.HoursSinceArticleCreated:
    case ConditionField.HoursSinceArticleUpdated:
    case ConditionField.HoursSinceArticlePublished:
      return 'duration';
    case ConditionField.ReminderCountSinceLastActivity:
      return 'number';
    case ConditionField.DueAt:
      return 'date';
    case ConditionField.CategoryId:
    case ConditionField.ArticleCollectionId:
      return 'tree';
    case ConditionField.AssignedToId:
      return 'id';
    default:
      return 'text';
  }
}

export function getValidOperatorsForField(field: ConditionField): ConditionOperator[] {
  const valueType = getConditionFieldValueType(field);
  switch (valueType) {
    case 'duration':
      return [
        ConditionOperator.GreaterThan,
        ConditionOperator.LessThan,
      ];
    case 'number':
      return [
        ConditionOperator.Equals,
        ConditionOperator.NotEquals,
        ConditionOperator.GreaterThan,
        ConditionOperator.LessThan,
      ];
    case 'boolean':
      return [ConditionOperator.Equals, ConditionOperator.NotEquals];
    case 'id':
      return [
        ConditionOperator.Equals,
        ConditionOperator.NotEquals,
        ConditionOperator.IsSet,
        ConditionOperator.IsNotSet,
      ];
    case 'tree':
      return [
        ConditionOperator.Equals,
        ConditionOperator.NotEquals,
        ConditionOperator.IsWithin,
        ConditionOperator.IsNotWithin,
        ConditionOperator.In,
        ConditionOperator.NotIn,
        ConditionOperator.IsSet,
        ConditionOperator.IsNotSet,
      ];
    case 'enum':
      return [
        ConditionOperator.Equals,
        ConditionOperator.NotEquals,
        ConditionOperator.In,
        ConditionOperator.NotIn,
      ];
    case 'date':
      return [
        ConditionOperator.Equals,
        ConditionOperator.NotEquals,
        ConditionOperator.GreaterThan,
        ConditionOperator.LessThan,
        ConditionOperator.IsSet,
        ConditionOperator.IsNotSet,
      ];
    default: // text
      return [
        ConditionOperator.Equals,
        ConditionOperator.NotEquals,
        ConditionOperator.Contains,
        ConditionOperator.NotContains,
        ConditionOperator.IsSet,
        ConditionOperator.IsNotSet,
      ];
  }
}

export function doesActionRequireConfig(actionType: AutomationActionType): boolean {
  const configRequired: AutomationActionType[] = [
    AutomationActionType.AssignToAgent,
    AutomationActionType.SetStatus,
    AutomationActionType.SetPriority,
    AutomationActionType.SetCategory,
    AutomationActionType.NotifySpecificAgents,
    AutomationActionType.SendEmailToSpecificAgents,
    AutomationActionType.AddFollower,
  ];
  return configRequired.includes(actionType);
}

// ── Event-based filtering for conditions and actions ──

const ticketEvents: AutomationEvent[] = [
  AutomationEvent.TicketCreated,
  AutomationEvent.TicketUpdated,
  AutomationEvent.TicketCommentAdded,
  AutomationEvent.TicketAssigned,
  AutomationEvent.TicketStatusChanged,
  AutomationEvent.TicketPriorityChanged,
  AutomationEvent.TicketCategoryChanged,
  AutomationEvent.ArticleLinkedToTicket, // ticket-scoped event
  AutomationEvent.TicketMerged,
];

const articleEvents: AutomationEvent[] = [
  AutomationEvent.ArticleFeedbackSubmitted,
  AutomationEvent.ArticleCreated,
  AutomationEvent.ArticleUpdated,
  AutomationEvent.ArticlePublished,
];

const ticketConditionFields: ConditionField[] = [
  ConditionField.Status, ConditionField.Priority, ConditionField.CategoryId,
  ConditionField.AssignedToId, ConditionField.Subject, ConditionField.Description,
  ConditionField.IsOverdue, ConditionField.DueAt,
  ConditionField.CustomerEmail, ConditionField.CustomerCompany,
  ConditionField.CreatedByType, ConditionField.IsCommentInternal,
];

const ticketTimeConditionFields: ConditionField[] = [
  ConditionField.HoursSinceCreated, ConditionField.HoursSinceUpdated,
  ConditionField.HoursSinceStatusChange, ConditionField.HoursSinceAssigned,
  ConditionField.HoursSinceLastActivity, ConditionField.HoursUntilDue,
  ConditionField.ReminderCountSinceLastActivity,
];

const articleConditionFields: ConditionField[] = [
  ConditionField.ArticleTitle, ConditionField.ArticleCollectionId, ConditionField.ArticleVisibility,
];

const articleTimeConditionFields: ConditionField[] = [
  ConditionField.HoursSinceArticleCreated, ConditionField.HoursSinceArticleUpdated,
  ConditionField.HoursSinceArticlePublished,
];

const allTicketFields: ConditionField[] = [...ticketConditionFields, ...ticketTimeConditionFields];
const allArticleFields: ConditionField[] = [...articleConditionFields, ConditionField.FeedbackIsHelpful, ...articleTimeConditionFields];

const ticketActionTypes: AutomationActionType[] = [
  AutomationActionType.AssignToAgent, AutomationActionType.AssignRoundRobin,
  AutomationActionType.Unassign, AutomationActionType.AssignLoadBalanced,
  AutomationActionType.SetStatus, AutomationActionType.SetPriority, AutomationActionType.SetCategory,
  AutomationActionType.NotifyAssignee, AutomationActionType.NotifyFollowers,
  AutomationActionType.NotifyAllAgents, AutomationActionType.NotifySpecificAgents,
  AutomationActionType.SendEmailToAssignee, AutomationActionType.SendEmailToFollowers,
  AutomationActionType.SendEmailToAllAgents, AutomationActionType.SendEmailToCustomer,
  AutomationActionType.SendEmailToSpecificAgents,
  AutomationActionType.AddFollower, AutomationActionType.RecordReminder,
];

const articleActionTypes: AutomationActionType[] = [
  AutomationActionType.NotifyArticleAuthor, AutomationActionType.SendEmailToArticleAuthor,
  AutomationActionType.NotifyAllAgents, AutomationActionType.SendEmailToAllAgents,
  AutomationActionType.NotifySpecificAgents, AutomationActionType.SendEmailToSpecificAgents,
];

export function getEntityContextFromConditions(fields: (ConditionField | null)[]): 'ticket' | 'article' | null {
  const nonNullFields = fields.filter((f): f is ConditionField => f !== null);
  if (nonNullFields.length === 0) return null;
  const hasArticleField = nonNullFields.some(f => allArticleFields.includes(f));
  const hasTicketField = nonNullFields.some(f => allTicketFields.includes(f));
  if (hasArticleField && !hasTicketField) return 'article';
  if (hasTicketField && !hasArticleField) return 'ticket';
  return null; // mixed or ambiguous
}

export function getValidConditionFieldsForEntityContext(context: 'ticket' | 'article' | null): ConditionField[] | null {
  if (context === 'ticket') return [...ticketConditionFields, ...ticketTimeConditionFields];
  if (context === 'article') return [...articleConditionFields, ...articleTimeConditionFields];
  return null;
}

export function getValidActionTypesForEntityContext(context: 'ticket' | 'article' | null): AutomationActionType[] | null {
  if (context === 'ticket') return ticketActionTypes;
  if (context === 'article') return articleActionTypes;
  return null;
}

export function isArticleEvent(event: AutomationEvent | null | undefined): boolean {
  if (event == null) return false;
  return articleEvents.includes(event);
}

export function getValidConditionFieldsForEvent(event: AutomationEvent | null | undefined): ConditionField[] | null {
  if (event == null) return null; // no filtering
  if (ticketEvents.includes(event)) return ticketConditionFields;
  if (event === AutomationEvent.ArticleFeedbackSubmitted) return [...articleConditionFields, ConditionField.FeedbackIsHelpful];
  if (articleEvents.includes(event)) return articleConditionFields;
  return null;
}

export function getValidActionTypesForEvent(event: AutomationEvent | null | undefined): AutomationActionType[] | null {
  if (event == null) return null; // no filtering
  if (ticketEvents.includes(event)) return ticketActionTypes;
  if (articleEvents.includes(event)) return articleActionTypes;
  return null;
}

// ── Nested DTOs ──

export interface ConditionGroupDto {
  conditions: ConditionDto[];
  sortOrder: number;
}

export interface ConditionDto {
  field: ConditionField;
  operator: ConditionOperator;
  value: string | null;
  sortOrder: number;
}

export interface ActionDto {
  actionType: AutomationActionType;
  configuration: string | null;
  sortOrder: number;
}

// ── Request DTOs ──

export interface CreateAutomationRuleRequest {
  name: string;
  description: string | null;
  ruleType: AutomationRuleType;
  triggerEvent: AutomationEvent | null;
  stopProcessing: boolean;
  repeatBehavior?: RepeatBehavior;
  conditionGroups: ConditionGroupDto[];
  actions: ActionDto[];
}

export interface UpdateAutomationRuleRequest {
  name: string | null;
  description: string | null;
  triggerEvent: AutomationEvent | null;
  stopProcessing: boolean | null;
  repeatBehavior?: RepeatBehavior | null;
  conditionGroups: ConditionGroupDto[] | null;
  actions: ActionDto[] | null;
}

export interface ToggleAutomationRuleRequest {
  isEnabled: boolean;
}

export interface RuleOrderItem {
  ruleId: string;
  sortOrder: number;
}

// ── Response DTOs ──

export interface AutomationRuleDetailResponse {
  id: string;
  name: string;
  description: string | null;
  ruleType: AutomationRuleType;
  triggerEvent: AutomationEvent | null;
  sortOrder: number;
  isEnabled: boolean;
  isDefault: boolean;
  stopProcessing: boolean;
  repeatBehavior: RepeatBehavior;
  createdAt: string;
  updatedAt: string;
  conditionGroups: ConditionGroupDetailResponse[];
  actions: ActionDetailResponse[];
}

export interface ConditionGroupDetailResponse {
  id: string;
  sortOrder: number;
  conditions: ConditionDetailResponse[];
}

export interface ConditionDetailResponse {
  id: string;
  field: ConditionField;
  operator: ConditionOperator;
  value: string | null;
  sortOrder: number;
}

export interface ActionDetailResponse {
  id: string;
  actionType: AutomationActionType;
  configuration: string | null;
  sortOrder: number;
}

export interface AutomationRuleListItem {
  id: string;
  name: string;
  description: string | null;
  ruleType: AutomationRuleType;
  triggerEvent: AutomationEvent | null;
  sortOrder: number;
  isEnabled: boolean;
  isDefault: boolean;
  stopProcessing: boolean;
  repeatBehavior: RepeatBehavior;
  conditionGroupCount: number;
  actionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AutomationRuleListResponse {
  rules: AutomationRuleListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ── Filter DTOs ──

export interface AutomationRuleFilterRequest {
  ruleType?: AutomationRuleType;
  isEnabled?: boolean;
  triggerEvent?: AutomationEvent;
  searchQuery?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface AutomationRuleLogFilterRequest {
  success?: boolean;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

// ── Log DTOs ──

// A log targets either a ticket (ticket rules) or an article (article rules); the other pair is null
export interface AutomationRuleLogResponse {
  id: string;
  automationRuleId: string;
  ticketId: string | null;
  ticketNumber: number | null;
  articleId: string | null;
  articleTitle: string | null;
  success: boolean;
  details: string | null;
  errorMessage: string | null;
  executedAt: string;
}

export interface AutomationRuleLogListResponse {
  logs: AutomationRuleLogResponse[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
